import { Accordion } from '@zillow/constellation-10';

import { useExpandedGroups } from '../../hooks/useExpandedGroups';

import { accordionItemStyles, accordionRootStyles } from './styles';

import type { ReactNode } from 'react';

type Props<T> = {
  items: Array<T>;
  getItemId: (item: T) => string;
  renderCard: (item: T, isExpanded: boolean) => ReactNode;
  // Distinct per list so expansion on one page doesn't carry over to another.
  storageKey: string;
  ariaLabel: string;
  'data-testid'?: string;
};

/**
 * A vertical list of independently expandable cards. Owns the Accordion wiring and the
 * session-persisted expanded state; callers supply the card itself.
 */
export const AccordionCardList = <T,>({
  items,
  getItemId,
  renderCard,
  storageKey,
  ariaLabel,
  'data-testid': dataTestId,
}: Props<T>) => {
  const { expandedIds, isExpanded, onExpandedChange } = useExpandedGroups(storageKey);

  return (
    <Accordion.Root
      data-testid={dataTestId}
      aria-label={ariaLabel}
      expanded={expandedIds}
      onExpandedChange={onExpandedChange}
      css={accordionRootStyles}
    >
      {items.map((item) => {
        const itemId = getItemId(item);

        return (
          <Accordion.Item key={itemId} value={itemId} css={accordionItemStyles}>
            {renderCard(item, isExpanded(itemId))}
          </Accordion.Item>
        );
      })}
    </Accordion.Root>
  );
};

export default AccordionCardList;
